import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import NumberFormat from "react-number-format"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Breadcrumb } from "../Breadcrumb"
import { Model } from "../../../models/model"
import "../EstadoResultado/estadoResultado.css"

const compactAmount = new Intl.NumberFormat('es-PE', { notation: 'compact', maximumFractionDigits: 1 })

export const VentasHistorico = () => {
    const { local_simbolo, local_separacion_miles, local_separacion_decimales } = useSelector(state => state.general.info)
    const [refresh, setRefresh] = useState(0)
    const [report, setReport] = useState(null)
    const [sort, setSort] = useState('anio')

    useEffect(() => {
        let active = true
        const load = async () => {
            const result = await Model.getAll({ url: 'reports/historical/sales' })
            if (active) setReport({ refresh, result })
        }
        load()
        return () => { active = false }
    }, [refresh])

    const loaded = report?.refresh === refresh
    const result = loaded ? report.result : null
    const currentYear = Number(new Intl.DateTimeFormat('es-PE', { year: 'numeric', timeZone: 'America/Lima' }).format(new Date()))
    const rows = (result?.data || []).map(row => ({ ...row, anio: Number(row.anio), total_venta: Number(row.total_venta) }))
    const years = new Map(rows.map(row => [row.anio, row]))
    const annualChange = row => {
        const previous = years.get(row.anio - 1)
        return row.anio < currentYear && previous?.total_venta > 0
            ? (row.total_venta - previous.total_venta) / previous.total_venta * 100 : null
    }
    const latestClosed = rows.find(row => row.anio < currentYear)
    const bestClosed = rows.reduce((best, row) => (
        row.anio < currentYear && (!best || row.total_venta > best.total_venta) ? row : best
    ), null)
    const sorted = [...rows].sort((a, b) => {
        if (a[sort] == null) return b[sort] == null ? b.anio - a.anio : 1
        if (b[sort] == null) return -1
        return Number(b[sort]) - Number(a[sort]) || b.anio - a.anio
    })
    const chartData = [...rows].reverse().map(row => ({
        ...row, label: row.anio === currentYear ? `${row.anio} · parcial` : String(row.anio),
    }))

    const amount = (value, percent = false) => value == null ? '—' : (
        <NumberFormat displayType="text" value={value}
            thousandSeparator={local_separacion_miles} decimalSeparator={local_separacion_decimales}
            prefix={percent ? '' : `${local_simbolo || 'S/'} `} suffix={percent ? ' %' : ''}
            decimalScale={2} fixedDecimalScale />
    )

    return (
        <div className="p-container er-report">
            <div className="mt-3">
                <Breadcrumb routes={[
                    { id: 1, name: 'Reportes', link: '/reportes', active: false },
                    { id: 2, name: 'Histórico de ventas', link: '/reportes/ventas/ph', active: true },
                ]} />
            </div>
            <header className="er-heading">
                <div>
                    <span className="er-eyebrow">Reportes de ventas</span>
                    <h1 className="p-title">Histórico de ventas</h1>
                    <p>¿Cómo evolucionaron las ventas, en qué año vendimos más y qué margen quedó?</p>
                </div>
                <button type="button" className="btn p-btn-secondary" disabled={!loaded} onClick={() => setRefresh(value => value + 1)}>
                    Actualizar
                </button>
            </header>
            {!loaded && <p role="status">Cargando histórico de ventas…</p>}
            {loaded && !result && <p role="alert">No se pudo cargar el reporte. Intenta actualizarlo.</p>}
            {result && !rows.length && <p role="status">No hay proyectos aprobados con fecha de asignación.</p>}
            {rows.length > 0 && (
                <>
                    <p className="er-source mb-3">Proyectos aprobados por año de asignación. Importes en moneda local.</p>
                    <div className="row g-2 mb-3">
                        {[
                            ['Ventas netas históricas', amount(result.total)],
                            ['Proyectos aprobados', rows.reduce((sum, row) => sum + Number(row.cantidad), 0)],
                            ['Año cerrado con más ventas', bestClosed ? <>{bestClosed.anio} · {amount(bestClosed.total_venta)}</> : '—'],
                            [latestClosed ? `Variación anual · ${latestClosed.anio}` : 'Variación anual', amount(latestClosed ? annualChange(latestClosed) : null, true)],
                        ].map(([label, value]) => (
                            <div className="col-6 col-lg-3" key={label}>
                                <div className="er-chart h-100 mb-0">
                                    <div className="er-source">{label}</div>
                                    <strong className="fs-5">{value}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                    <figure className="er-chart">
                        <figcaption className="fw-bold mb-2">Ventas netas por año</figcaption>
                        <div className="er-chart-scroll">
                            <ResponsiveContainer width="100%" height={250} minWidth={Math.max(360, chartData.length * 70)}>
                                <BarChart data={chartData}>
                                    <CartesianGrid vertical={false} stroke="#ece4ee" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tickFormatter={value => compactAmount.format(value)} />
                                    <Tooltip formatter={value => amount(value)} />
                                    <Bar dataKey="total_venta" name="Ventas netas" fill="var(--primary-color)" maxBarSize={44} isAnimationActive={false} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </figure>
                    <div className="mb-2">
                        <label className="form-label" htmlFor="historical-sales-sort">Ordenar por</label>
                        <select className="form-select w-auto" id="historical-sales-sort" value={sort} onChange={event => setSort(event.target.value)}>
                            <option value="anio">Año</option>
                            <option value="total_venta">Ventas netas</option>
                            <option value="margen">Margen directo</option>
                        </select>
                    </div>
                    <p className="er-source mb-2">
                        La variación compara años completos consecutivos con ventas positivas en el año anterior.
                        El margen directo descuenta los costos directos registrados.
                    </p>
                    <div className="table-responsive er-table-wrap" tabIndex={0} role="region" aria-label="Ventas por año">
                        <table className="table er-table mb-0">
                            <thead><tr>
                                <th scope="col">Año</th>
                                <th scope="col" className="text-end">Ventas netas</th>
                                <th scope="col" className="text-end">% del histórico</th>
                                <th scope="col" className="text-end">Variación anual</th>
                                <th scope="col" className="text-end">Proyectos</th>
                                <th scope="col" className="text-end">Margen directo</th>
                            </tr></thead>
                            <tbody>
                                {sorted.map(row => {
                                    const change = annualChange(row)
                                    return (
                                        <tr key={row.anio}>
                                            <th scope="row">{row.anio}{row.anio === currentYear && <small className="er-source ms-2">Parcial</small>}</th>
                                            <td className="text-end text-nowrap">{amount(row.total_venta)}</td>
                                            <td className="text-end">{amount(row.percent, true)}</td>
                                            <td className={`text-end ${change < 0 ? 'text-danger' : ''}`}>{amount(change, true)}</td>
                                            <td className="text-end">{row.cantidad}</td>
                                            <td className={`text-end ${Number(row.margen) < 0 ? 'text-danger' : ''}`}>{amount(row.margen, true)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )
}
